import React, { useEffect, useMemo } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { Chip, Tabs, Text, toast } from "@performyard/py-components";

import Breadcrumbs from "../../../../components/Breadcrumbs";
import CompetenciesTab from "./CompetenciesTab";
import EmployeesTab from "./EmployeesTab";

import { useRole } from "../../../../api/hooks/defs/roles";

import { ROLES_LIST_PATH } from "../CreateEditRole/validationSchema";

export type RoleDetailTab = "competencies" | "employees";

const VALID_TABS: RoleDetailTab[] = ["competencies", "employees"];

const isValidTab = (value: string | null): value is RoleDetailTab =>
	value !== null && (VALID_TABS as string[]).includes(value);

const RoleDetail: React.FC = () => {
	const { roleId = "" } = useParams<{ roleId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	const tabParam = searchParams.get("tab");
	const activeTab: RoleDetailTab = isValidTab(tabParam) ? tabParam : "competencies";

	const { data, isLoading, error } = useRole(roleId);
	const isArchived = data?.role?.status === "archived";

	useEffect(() => {
		if (error) toast.error("Role not found");
	}, [error]);

	// Hooks must run unconditionally and in the same order on every render — keep this above
	// the loading/error early returns. Keying on `roleId` (always defined from useParams) instead
	// of `role._id` lets us memoize before `role` has resolved.
	const tabConfig = useMemo(
		() => [
			{
				value: "competencies",
				label: "Competencies",
				content: <CompetenciesTab roleId={roleId} readOnly={isArchived} />,
			},
			{
				value: "employees",
				label: "Employees",
				content: <EmployeesTab roleId={roleId} readOnly={isArchived} />,
			},
		],
		[roleId, isArchived]
	);

	if (error) return <Navigate to={ROLES_LIST_PATH} replace />;

	if (isLoading) {
		return <div data-testid="role-detail-loading" />;
	}

	const role = data?.role;
	if (!role) {
		return <div data-testid="role-detail-loading" />;
	}

	const handleTabChange = (next: string) => {
		const newParams = new URLSearchParams(searchParams);
		if (next === "competencies") {
			newParams.delete("tab");
		} else {
			newParams.set("tab", next);
		}
		setSearchParams(newParams, { replace: false });
	};

	return (
		<div className="px-6 py-8">
			<Breadcrumbs
				pages={[
					{ pageID: "breadcrumb-roles", label: "Roles", url: ROLES_LIST_PATH },
					{ pageID: "breadcrumb-role-name", label: role.name, url: "" },
				]}
				className="mb-4"
			/>

			<div className="mb-6 flex items-baseline gap-3">
				<Text size="sub-heading" weight="semi-bold" color="neutral-9">
					{role.name}
				</Text>
				{role.status === "archived" && <Chip label="Archived" color="neutral" size={20} />}
			</div>

			<Tabs value={activeTab} onValueChange={handleTabChange} tabs={tabConfig} size="large" />
		</div>
	);
};

export default RoleDetail;
